export interface WidgetEvent {
  eventName: string;
  data: { [key: string]: any };
  timestamp: number;
}

export type EventListener = (event: WidgetEvent) => void;

export class EventManager {
  private static _instance: EventManager;
  private _listeners: Map<string, EventListener[]> = new Map();
  private _allEventListeners: EventListener[] = [];

  public static getInstance(): EventManager {
    if (!EventManager._instance) {
      EventManager._instance = new EventManager();
    }
    return EventManager._instance;
  }

  public on(eventName: string, listener: EventListener): void {
    if (!this._listeners.has(eventName)) {
      this._listeners.set(eventName, []);
    }
    this._listeners.get(eventName)!.push(listener);
  }

  public onAll(listener: EventListener): void {
    this._allEventListeners.push(listener);
  }

  public off(eventName: string, listener?: EventListener): void {
    if (!this._listeners.has(eventName)) return;

    if (!listener) {
      this._listeners.delete(eventName);
      return;
    }

    const listeners = this._listeners.get(eventName)!;
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }

    if (listeners.length === 0) {
      this._listeners.delete(eventName);
    }
  }

  public offAll(listener?: EventListener): void {
    if (!listener) {
      this._allEventListeners = [];
      return;
    }

    const index = this._allEventListeners.indexOf(listener);
    if (index > -1) {
      this._allEventListeners.splice(index, 1);
    }
  }

  public emit(eventName: string, data: { [key: string]: any } = {}): void {
    const event: WidgetEvent = {
      eventName,
      data,
      timestamp: Date.now()
    };

    // Notify all-event listeners first
    this._allEventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error(`Error in all-event listener for '${eventName}':`, error);
      }
    });

    // Then notify specific event listeners
    if (this._listeners.has(eventName)) {
      const listeners = this._listeners.get(eventName)!;
      listeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {
          console.error(`Error in event listener for '${eventName}':`, error);
        }
      });
    }
  }

  public getAllListeners(): Map<string, EventListener[]> {
    return new Map(this._listeners);
  }

  public removeAllListeners(): void {
    this._listeners.clear();
  }
}